import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { MODULES, PERMISSION_ACTIONS } from '@shared/constants';
import { getModulePermission } from '@shared/utils';

import { ModuleGuard } from './modules/core/guards/module.guard';
import { PermissionGuard } from './modules/core/guards/permission.guard';
import { IAMGuard } from './modules/setup/iam-config/guards/iam.guard';
import { UserManagementGuard } from './modules/setup/iam-config/guards/user-management.guard';
import {
  IAMUserManagement,
  USER_MGT_FEATURES,
} from './modules/setup/iam-config/services/iam-config.service';
import { StorageGuard } from './modules/setup/storage/guards/storage.guard';
import { ThankingPageComponent } from './modules/virtual-module/request-document/popups/thanking-page/thanking-page.component';
import { AboutComponent } from './pages/about/about.component';
import { ViewReleaseNoteComponent } from './pages/about/popups/view-release-note/view-release-note.component';
import { AccessDeniedComponent } from './pages/access-denied/access-denied.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { DeactivateModuleComponent } from './pages/deactivate-module/deactivate-module.component';
import { HomeComponent } from './pages/home/home.component';
import { MaintenanceComponent } from './pages/maintenance/maintenance.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { PersonalizationComponent } from './pages/personalization/personalization.component';
import { ProfileComponent } from './pages/profile/profile.component';
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component';
import { AppGuard } from './app.guard';

const routes_bms_operations: Routes = [
  {
    path: 'products',
    loadChildren: () =>
      import('./modules/products/products.module').then(
        (m) => m.ProductsModule
      ),
    canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
    data: {
      module: MODULES.PRODUCTS,
      allowedPermissions: getModulePermission(MODULES.PRODUCTS, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'insurers',
    loadChildren: () =>
      import('./modules/insurers/insurers.module').then(
        (m) => m.InsurersModule
      ),
    canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
    data: {
      module: MODULES.INSURERS,
      allowedPermissions: getModulePermission(MODULES.INSURERS, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'leads',
    loadChildren: () =>
      import('./modules/leads/leads.module').then((m) => m.LeadsModule),
    canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
    data: {
      module: MODULES.LEADS,
      allowedPermissions: getModulePermission(MODULES.LEADS, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'cover-notes',
    loadChildren: () =>
      import('./modules/cover-notes/cover-notes.module').then(
        (m) => m.CoverNoteModule
      ),
    canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
    data: {
      module: MODULES.COVER_NOTES,
      allowedPermissions: getModulePermission(MODULES.COVER_NOTES, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'customers',
    loadChildren: () =>
      import('./modules/customers/customers.module').then(
        (m) => m.CustomersModule
      ),
    canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
    data: {
      module: MODULES.CUSTOMERS,
      allowedPermissions: getModulePermission(MODULES.CUSTOMERS, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'claims',
    loadChildren: () =>
      import('./modules/claims/claims.module').then((m) => m.ClaimsModule),
    canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
    data: {
      module: MODULES.CLAIMS,
      allowedPermissions: getModulePermission(MODULES.CLAIMS, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'endorsements',
    loadChildren: () =>
      import('./modules/endorsements/endorsements.module').then(
        (m) => m.EndorsementsModule
      ),
    canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
    data: {
      module: MODULES.ENDORSEMENT,
      allowedPermissions: getModulePermission(MODULES.ENDORSEMENT, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'reports',
    loadChildren: () =>
      import('./modules/reports/reports.module').then((m) => m.ReportsModule),
    canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
    data: {
      module: MODULES.ENDORSEMENT,
      allowedPermissions: getModulePermission(MODULES.REPORTS, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  // {
  //   path: 'users',
  //   loadChildren: () =>
  //     import('./modules/users/users.module').then(
  //       (m) => m.ExternalUsersModule
  //     ),
  //   canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
  //   data: {
  //     module: MODULES.EXTERNAL_USERS,
  //     allowedPermissions: getModulePermission(MODULES.USERS, [
  //       PERMISSION_ACTIONS.READ,
  //     ]),
  //   },
  // },
  {
    path: 'external-users',
    loadChildren: () =>
      import('./modules/external-users/external-users.module').then(
        (m) => m.ExternalUsersModule
      ),
    canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
    data: {
      module: MODULES.EXTERNAL_USERS,
      allowedPermissions: getModulePermission(MODULES.EXTERNAL_USERS, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
];
const routes_bms_finance: Routes = [
  {
    path: 'bms-invoices',
    loadChildren: () =>
      import('./modules/invoices/invoices.module').then(
        (m) => m.InvoicesModule
      ),
    canActivate: [ModuleGuard, PermissionGuard],
    data: {
      module: MODULES.INVOICES,
      allowedPermissions: getModulePermission(MODULES.INVOICES, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'bulk',
    loadChildren: () =>
      import('./modules/invoice-bulk/invoice-bulk.module').then(
        (m) => m.InvoicesBulkModule
      ),
    canActivate: [ModuleGuard, PermissionGuard],
    data: {
      module: MODULES.INVOICES,
      allowedPermissions: getModulePermission(MODULES.INVOICES, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'commissions',
    loadChildren: () =>
      import('./modules/commissions/commissions.module').then(
        (m) => m.CommissionsModule
      ),
    canActivate: [ModuleGuard, PermissionGuard],
    data: {
      module: MODULES.INVOICES,
      allowedPermissions: getModulePermission(MODULES.COMMISSIONS, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'credit-debit-notes',
    loadChildren: () =>
      import('./modules/credit-debit-notes/credit-debit-notes.module').then(
        (m) => m.CreditDebitNotesModule
      ),
    canActivate: [ModuleGuard, PermissionGuard],
    data: {
      module: MODULES.CREDIT_DEBIT_NOTES,
      allowedPermissions: getModulePermission(MODULES.CREDIT_DEBIT_NOTES, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },

  {
    path: 'receipts',
    loadChildren: () =>
      import('./modules/receipts/receipts.module').then(
        (m) => m.ReceiptsModule
      ),
    canActivate: [ModuleGuard, PermissionGuard],
    data: {
      module: MODULES.RECEIPTS,
      allowedPermissions: getModulePermission(MODULES.RECEIPTS, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'reconciliations',
    loadChildren: () =>
      import('./modules/reconciliation/reconciliation.module').then(
        (m) => m.ReconciliationModule
      ),
    canActivate: [ModuleGuard, PermissionGuard],
    data: {
      module: MODULES.RECEIPTS,
      allowedPermissions: getModulePermission(MODULES.RECONCILIATIONS, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'commission-codes',
    loadChildren: () =>
      import('./modules/commissions-code/commission-codes.module').then(
        (m) => m.CommissionCodesModule
      ),
    canActivate: [ModuleGuard, PermissionGuard],
    data: {
      module: MODULES.COMMISSION_CODES,
      allowedPermissions: getModulePermission(MODULES.COMMISSION_CODES, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
  {
    path: 'agent-commission-codes',
    loadChildren: () =>
      import(
        './modules/agent-commission-codes/agent-commission-codes.module'
      ).then((m) => m.AgentCommissionCodesModule),
    canActivate: [ModuleGuard, PermissionGuard],
    data: {
      module: MODULES.AGENT_COMMISSION_CODES,
      allowedPermissions: getModulePermission(MODULES.AGENT_COMMISSION_CODES, [
        PERMISSION_ACTIONS.READ,
      ]),
    },
  },
];

const routes: Routes = [
  {
    path: '',
    redirectTo: '/app/launchpad',
    pathMatch: 'full',
  },
  {
    path: 'app',
    redirectTo: '/app/launchpad',
    pathMatch: 'full',
  },
  {
    path: 'app/auth',
    loadChildren: () =>
      import('./modules/auth/auth.module').then((m) => m.AuthModule),
  },
  // {
  //   path: 'collection-info/:id',
  //   component: RequestDocumentInformationCollectionPageComponent,
  // },
  {
    path: 'thank-you',
    component: ThankingPageComponent,
  },
  {
    path: 'app/checkout',
    loadChildren: () =>
      import('./modules/setup/payment-gateways/checkout/checkout.module').then(
        (m) => m.CheckoutModule
      ),
  },
  {
    path: 'app/preview',
    loadChildren: () =>
      import('./modules/preview/preview.module').then((m) => m.PreviewModule),
  },
  {
    path: 'app',
    component: DashboardComponent,
    canActivate: [AppGuard],
    children: [
      {
        path: 'launchpad',
        component: HomeComponent,
        data: {
          title: 'app.launchpad',
          goBackEnabled: false,
          searchBarEnabled: false,
        },
      },
      {
        path: 'about',
        component: AboutComponent,
        data: {
          title: 'app.about',
          goBackEnabled: true,
        },
      },
      {
        path: 'view-release-note',
        component: ViewReleaseNoteComponent,
        data: {
          title: 'view-release-note',
          goBackEnabled: true,
        },
      },
      {
        path: 'personalization',
        component: PersonalizationComponent,
        data: {
          title: 'Personalization',
          goBackEnabled: true,
        },
      },
      {
        path: 'profile',
        component: ProfileComponent,
        data: {
          title: 'app.profile',
          goBackEnabled: true,
        },
      },
      {
        path: 'profile/reset-password',
        component: ResetPasswordComponent,
        canActivate: [UserManagementGuard],
        data: {
          title: 'app.reset-password',
          goBackEnabled: true,
          config: USER_MGT_FEATURES.SELF_PASSWORD_RESET,
          configCb: (data: IAMUserManagement) => {
            return data[USER_MGT_FEATURES.SELF_PASSWORD_RESET];
          },
        },
      },
      {
        path: 'commission-codes',
        loadChildren: () =>
          import('./modules/commissions-code/commission-codes.module').then(
            (m) => m.CommissionCodesModule
          ),
        canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
        data: {
          module: MODULES.COMMISSION_CODES,
          allowedPermissions: getModulePermission(MODULES.COMMISSION_CODES, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'agent-commission-codes',
        loadChildren: () =>
          import(
            './modules/agent-commission-codes/agent-commission-codes.module'
          ).then((m) => m.AgentCommissionCodesModule),
        canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
        data: {
          module: MODULES.AGENT_COMMISSION_CODES,
          allowedPermissions: getModulePermission(
            MODULES.AGENT_COMMISSION_CODES,
            [PERMISSION_ACTIONS.READ]
          ),
        },
      },
      {
        path: 'apps',
        loadChildren: () =>
          import('./modules/connected-apps/connected-apps.module').then(
            (m) => m.ConnectedAppsModule
          ),
        canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
        data: {
          module: MODULES.APPS,
          allowedPermissions: getModulePermission(MODULES.APPS, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'connections',
        loadChildren: () =>
          import('./modules/connections/connections.module').then(
            (m) => m.ConnectionsModule
          ),
        canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
        data: {
          module: MODULES.CONNECTIONS,
          allowedPermissions: getModulePermission(MODULES.CONNECTIONS, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'authorization',
        loadChildren: () =>
          import('./modules/permissions/permissions.module').then(
            (m) => m.PermissionModule
          ),
      },
      {
        path: 'reference-categories',
        loadChildren: () =>
          import('./modules/references/references.module').then(
            (m) => m.ReferencesModule
          ),
      },
      {
        path: 'setup',
        loadChildren: () =>
          import('./modules/setup/setup.module').then((m) => m.SetupModule),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.SETUP,
          allowedPermissions: getModulePermission(MODULES.IDENTITIES, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'identities',
        loadChildren: () =>
          import('./modules/identities/identities.module').then(
            (m) => m.IdentitiesModule
          ),
        canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
        data: {
          module: MODULES.IDENTITIES,
          allowedPermissions: getModulePermission(MODULES.IDENTITIES, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'knowledgebase',
        loadChildren: () =>
          import('./modules/knowledgebase/knowledgebase.module').then(
            (m) => m.KnowledgebaseModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.KNOWLEDGEBASE,
          allowedPermissions: getModulePermission(MODULES.KNOWLEDGEBASE, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'entities',
        loadChildren: () =>
          import('./modules/saas-tenant/saas-tenant.module').then(
            (m) => m.SaasTenetModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.SAAS_TENANT,
          allowedPermissions: getModulePermission(MODULES.SAAS_TENANT, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'saas-users',
        loadChildren: () =>
          import('./modules/saas-users/saas-users.module').then(
            (m) => m.SaasUsersModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.SAAS_USERS,
          allowedPermissions: getModulePermission(MODULES.SAAS_USERS, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'users',
        loadChildren: () =>
          import('./modules/saas-users/saas-users.module').then(
            (m) => m.SaasUsersModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.USERS,
          allowedPermissions: getModulePermission(MODULES.USERS, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'plans',
        loadChildren: () =>
          import('./modules/saas-subscription/saas-subscription.module').then(
            (m) => m.SaasSubscriptionModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.SAAS_SUBSCRIPTIONS,
          allowedPermissions: getModulePermission(MODULES.SAAS_SUBSCRIPTIONS, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'invoices',
        loadChildren: () =>
          import('./modules/saas-invoice/saas-invoice.module').then(
            (m) => m.SaasInvoiceModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.SAAS_INVOICES,
          allowedPermissions: getModulePermission(MODULES.SAAS_INVOICES, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'payments',
        loadChildren: () =>
          import('./modules/saas-payment/saas-payment.module').then(
            (m) => m.SaasPaymentModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.SAAS_PAYMENTS,
          allowedPermissions: getModulePermission(MODULES.SAAS_PAYMENTS, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'saas-settings',
        loadChildren: () =>
          import('./modules/saas-settings/saas-settings.module').then(
            (m) => m.SaasSettingsModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.SAAS_SETTINGS,
          allowedPermissions: getModulePermission(MODULES.SAAS_SETTINGS, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'notices',
        loadChildren: () =>
          import('./modules/notices/notices.module').then(
            (m) => m.NoticesModule
          ),
        canActivate: [ModuleGuard],
        data: {
          module: MODULES.NOTICES,
        },
      },
      {
        path: 'virtual-modules',
        loadChildren: () =>
          import('./modules/virtual-module/virtual-module.module').then(
            (m) => m.VirtualModuleModule
          ),
      },
      {
        path: 'logs',
        loadChildren: () =>
          import('./modules/logs/logs.module').then((m) => m.LogsModule),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.LOGS,
          allowedPermissions: getModulePermission(MODULES.LOGS, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'messages',
        loadChildren: () =>
          import('./modules/messages/message.module').then(
            (m) => m.MessageModule
          ),
      },
      {
        path: 'storage',
        loadChildren: () =>
          import('./modules/storage/storage.module').then(
            (m) => m.StorageModule
          ),
        canLoad: [StorageGuard],
      },
      {
        path: 'additional-storage',
        loadChildren: () =>
          import('./modules/additional-storage/additional-storage.module').then(
            (m) => m.AdditionalStorageModule
          ),
        canLoad: [StorageGuard],
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.ADDITIONAL_STORAGE,
        },
      },
      {
        path: 'notifications',
        loadChildren: () =>
          import('./modules/notifications/notifications.module').then(
            (m) => m.NotificationsModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.NOTIFICATIONS,
          allowedPermissions: getModulePermission(MODULES.NOTIFICATIONS, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'mailbox',
        loadChildren: () =>
          import('./modules/mailbox/mailbox.module').then(
            (m) => m.MailboxModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.MAILBOX,
          allowedPermissions: getModulePermission(MODULES.MAILBOX, [
            PERMISSION_ACTIONS.READ,
            PERMISSION_ACTIONS.WRITE,
          ]),
        },
      },
      {
        path: 'cover-notes',
        loadChildren: () =>
          import('./modules/cover-notes/cover-notes.module').then(
            (m) => m.CoverNoteModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.COVER_NOTES,
          allowedPermissions: getModulePermission(MODULES.COVER_NOTES, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'mailbox',
        loadChildren: () =>
          import('./modules/mailbox/mailbox.module').then(
            (m) => m.MailboxModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.MAILBOX,
          allowedPermissions: getModulePermission(MODULES.MAILBOX, [
            PERMISSION_ACTIONS.READ,
            PERMISSION_ACTIONS.WRITE,
          ]),
        },
      },
      {
        path: 'modules',
        loadChildren: () =>
          import('./modules/modules/modules.module').then(
            (m) => m.ModulesModule
          ),
        canActivate: [ModuleGuard, PermissionGuard],
        data: {
          module: MODULES.MODULES,
          allowedPermissions: getModulePermission(MODULES.MODULES, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
      {
        path: 'integrations',
        loadChildren: () =>
          import('./modules/integrations/integrations.module').then(
            (m) => m.IntegrationsModule
          ),
      },
      ...routes_bms_operations,
      ...routes_bms_finance,
      {
        path: 'virtual-modules',
        loadChildren: () =>
          import('./modules/virtual-module/virtual-module.module').then(
            (m) => m.VirtualModuleModule
          ),
      },
      {
        path: 'deactivated',
        component: DeactivateModuleComponent,
        data: {
          goBackEnabled: true,
        },
      },
      {
        path: 'access-denied',
        component: AccessDeniedComponent,
        data: {
          title: 'pages.access-denied.error-title',
          goBackEnabled: true,
        },
      },
      {
        path: 'brokers',
        loadChildren: () =>
          import('./modules/brokers/brokers.module').then(
            (m) => m.BrokersModule
          ),
        canActivate: [ModuleGuard, PermissionGuard, IAMGuard],
        data: {
          module: MODULES.BROKERS,
          allowedPermissions: getModulePermission(MODULES.BROKERS, [
            PERMISSION_ACTIONS.READ,
          ]),
        },
      },
    ],
  },
  {
    path: 'maintenance',
    component: MaintenanceComponent,
  },
  {
    path: 'access-denied',
    component: AccessDeniedComponent,
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {})],
  exports: [RouterModule],
})
export class AppRoutingModule {}
